import { useQuery } from "@tanstack/react-query";
import { ArrowDownToLine, ArrowUpFromLine, RefreshCw, AlertCircle } from "lucide-react";
import { getStockHistory, type StockMovement } from "@/api/stock";
import { Panel } from "@/components/Panel";

const TYPE_LABELS: Record<string, string> = {
  restock: "Wareneingang",
  adjustment: "Korrektur",
  pick: "Kommissionierung",
  return: "Retoure",
  manual: "Manuell",
  cascade: "Kaskade",
  reserve: "Reservierung",
};

function fmtDateTime(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(+d)) return s;
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function MovementIcon({ type, qty }: { type: string; qty: number }) {
  if (qty > 0) return <ArrowDownToLine className="size-3.5 text-success" />;
  if (qty < 0) return <ArrowUpFromLine className="size-3.5 text-coral" />;
  if (type === "adjustment") return <AlertCircle className="size-3.5 text-warn" />;
  return <RefreshCw className="size-3.5 text-muted" />;
}

export function MovementHistory({ sku }: { sku: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["stock-history", sku],
    queryFn: () => getStockHistory(sku, { limit: 50 }),
    enabled: !!sku,
  });

  return (
    <Panel
      title="Bewegungen"
      subtitle={`${data?.pagination.total ?? 0} Einträge · Artikel ${sku}`}
    >
      {isLoading ? (
        <p className="text-sm text-ink-2 text-center py-6">Lade Verlauf…</p>
      ) : !data || data.data.length === 0 ? (
        <p className="text-sm text-ink-2 text-center py-6">
          Keine Bewegungen für diesen Artikel.
        </p>
      ) : (
        <ul className="divide-y divide-line-2">
          {data.data.map((m: StockMovement) => (
            <li
              key={m.id}
              className="grid grid-cols-[140px_120px_60px_60px_1fr] gap-3 py-2.5 text-sm items-center"
            >
              <div className="font-mono text-[11px] text-muted">
                {fmtDateTime(m.created_at)}
              </div>
              <div className="flex items-center gap-1.5">
                <MovementIcon type={m.type} qty={m.quantity} />
                <span className="text-ink-2">{TYPE_LABELS[m.type] ?? m.type}</span>
              </div>
              <div
                className={`font-mono text-sm font-semibold text-right ${
                  m.quantity > 0
                    ? "text-success"
                    : m.quantity < 0
                      ? "text-coral"
                      : "text-ink-2"
                }`}
              >
                {m.quantity > 0 ? "+" : ""}
                {m.quantity}
              </div>
              <div className="font-mono text-xs text-muted text-right">
                {m.quantity_before} → {m.quantity_after}
              </div>
              <div className="text-xs text-ink-2 truncate">
                {m.note ?? <span className="text-muted">—</span>}
                {m.order_id && (
                  <span className="ml-2 font-mono text-[11px] text-primary">
                    Order #{m.order_id}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
